class SideDrawer extends HTMLElement {
    constructor() {
        super();
        this.isOpen = false;
        this.modalId = this.dataset.modal;
        this.backdrop = this.querySelector('[data-modal="bg"]');
        this.content = this.querySelector('[data-modal="content"]');
        this.closeBtns = this.querySelectorAll('[data-modal="close"]');
        this.triggerElement = null;
        this.openTl = null;
        this.closeTl = null;
        this.inertedElements = [];

        this.handleDocumentClick = this.handleDocumentClick.bind(this);
        this.onKeyUp = this.onKeyUp.bind(this);
        this.onResize = this.onResize.bind(this);
        this.desktopQuery = window.matchMedia('(min-width: 1100px)');
    }

    get prefersReducedMotion() {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    connectedCallback() {
        this.setAttribute('aria-hidden', 'true');
        this.hidden = true;

        this.createTimelines();

        this.closeBtns.forEach((btn) => btn.addEventListener('click', () => this.close()));
        this.backdrop?.addEventListener('click', () => this.close());
        this.addEventListener('keyup', this.onKeyUp);

        document.addEventListener('click', this.handleDocumentClick);
        this.desktopQuery.addEventListener('change', this.onResize);
        this.syncDetailsForViewport();
    }

    disconnectedCallback() {
        document.removeEventListener('click', this.handleDocumentClick);
        this.desktopQuery.removeEventListener('change', this.onResize);
        this.clearInert();
        document.body.classList.remove('overflow-hidden');
    }

    onResize() {
        this.syncDetailsForViewport();
        this.syncPanelWidth();
    }

    onKeyUp(evt) {
        if (!this.isOpen) return;
        if (evt.code === 'Escape' || evt.code === 'Enter') this.close();
    }

    handleDocumentClick(e) {
        const trigger = e.target.closest(`[data-drawer-trigger="${this.modalId}"]`);
        if (!trigger) return;

        if (trigger.tagName === 'A') {
            const href = trigger.getAttribute('href');
            if (href && href !== '#' && !href.startsWith('javascript:')) return;
        }

        e.preventDefault();
        this.open(trigger);
    }

    createTimelines() {
        if (!this.content) return;

        this.openTl?.kill();
        this.closeTl?.kill();

        this.openTl = gsap.timeline({
            paused: true,
            defaults: { ease: 'menu-hover' },
            onStart: () => {
                this.hidden = false;
                this.style.display = 'flex';
                this.setAttribute('aria-hidden', 'false');
            },
            onComplete: () => {
                this.isOpen = true;
                trapFocus(this, this.content);
            },
        });

        this.openTl
            .fromTo(
                this.content,
                { y: 12, opacity: 0 },
                { y: 0, opacity: 1, duration: 0.25 }
            )
            .fromTo(
                this.backdrop,
                { opacity: 0 },
                { opacity: 0.3, duration: 0.25 },
                '<'
            );

        this.closeTl = gsap.timeline({
            paused: true,
            defaults: { ease: 'menu-hover' },
            onStart: () => {
                this.isOpen = false;
            },
            onComplete: () => {
                this.hidden = true;
                this.style.display = 'none';
                this.setAttribute('aria-hidden', 'true');
            },
        });

        this.closeTl
            .fromTo(
                this.content,
                { y: 0, opacity: 1 },
                { y: 12, opacity: 0, duration: 0.25 }
            )
            .fromTo(
                this.backdrop,
                { opacity: 0.3 },
                { opacity: 0, duration: 0.25 },
                '<'
            );
    }

    syncPanelWidth() {
        const headerWrapper = document.querySelector('header-dropdown');

        if (window.innerWidth > 1100 && headerWrapper) {
            const headerDropdownWidth = headerWrapper.offsetWidth;
            this.content.style.width = `${headerDropdownWidth}px`;
            this.content.style.maxWidth = `${headerDropdownWidth}px`;
            return;
        }

        this.content.style.width = '';
        this.content.style.maxWidth = '';
    }

    syncDetailsForViewport() {
        const isDesktop = this.desktopQuery.matches;

        this.querySelectorAll('[data-details-filter]').forEach((details) => {
            details.toggleAttribute('open', isDesktop);
        });
    }

    openFacet(trigger) {
        const facetKey = trigger?.dataset.openFacet;
        if (!facetKey) return;

        if (!this.desktopQuery.matches) {
            this.querySelectorAll('[data-details-filter]').forEach((details) => {
                details.removeAttribute('open');
            });
        }

        const details = this.querySelector(`[data-details-filter][id^="Details-${facetKey}-"]`);
        details?.setAttribute('open', '');
    }

    open(trigger) {
        if (this.openTl?.isActive()) return;

        if (this.isOpen) {
            this.openFacet(trigger);
            return;
        }

        this.triggerElement = trigger;

        this.syncDetailsForViewport();
        this.openFacet(trigger);
        this.syncPanelWidth();
        this.setTriggersExpanded(true);
        this.applyInert();
        document.body.classList.add('overflow-hidden');

        if (this.prefersReducedMotion) {
            this.isOpen = true;
            this.openInstant();
            return;
        }

        this.openTl.play(0);

        requestAnimationFrame(() => {
            if (typeof ScrollTrigger === 'undefined') return;
            ScrollTrigger.getAll().forEach((st) => {
                if (this.contains(st.trigger)) st.refresh();
            });
        });
    }

    openInstant() {
        this.hidden = false;
        this.style.display = 'flex';
        this.setAttribute('aria-hidden', 'false');
        gsap.set(this.content, { y: 0, opacity: 1 });
        gsap.set(this.backdrop, { opacity: 0.3 });
        trapFocus(this, this.content);
    }

    close() {
        if (!this.isOpen) return;

        this.isOpen = false;
        this.setTriggersExpanded(false);
        this.clearInert();
        document.body.classList.remove('overflow-hidden');
        removeTrapFocus(this.triggerElement);

        if (this.prefersReducedMotion) {
            this.closeInstant();
            return;
        }

        this.closeTl.play(0);
    }

    closeInstant() {
        gsap.set(this.content, { y: 12, opacity: 0 });
        gsap.set(this.backdrop, { opacity: 0 });
        this.hidden = true;
        this.style.display = 'none';
        this.setAttribute('aria-hidden', 'true');
    }

    setTriggersExpanded(expanded) {
        document.querySelectorAll(`[data-drawer-trigger="${this.modalId}"]`).forEach((trigger) => {
            trigger.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        });
    }

    applyInert() {
        this.clearInert();
        this.inertedElements = Array.from(document.body.children).filter((el) => {
            if (el === this || el.contains(this) || el.nodeName === 'SCRIPT') return false;
            if (el.tagName === 'HEADER' || el.querySelector('header')) return false;
            if (el.hasAttribute('data-header-underlay') || el.querySelector('[data-header-underlay]')) return false;
            return true;
        });
        this.inertedElements.forEach((el) => {
            el.inert = true;
        });
    }

    clearInert() {
        this.inertedElements.forEach((el) => {
            el.inert = false;
        });
        this.inertedElements = [];
    }
}

customElements.define('side-drawer', SideDrawer);
